import type { IJwt } from '../services/jwt';

export const WHATSAPP_ENTRY_TOKEN_TYPE = 'whatsapp_entry' as const;
export const WHATSAPP_ENTRY_SOURCE = 'whatsapp' as const;
export const WHATSAPP_ENTRY_TTL_SECONDS = 10 * 60;

export const WHATSAPP_ENTRY_PURPOSES = {
    EXPRESS: 'express',
    JOB: 'job',
    SMART_AGENT: 'smart_agent',
} as const;

export type WhatsAppEntryPurpose =
    typeof WHATSAPP_ENTRY_PURPOSES[keyof typeof WHATSAPP_ENTRY_PURPOSES];

export type WhatsAppEntryJwtPayload = IJwt & {
    type: typeof WHATSAPP_ENTRY_TOKEN_TYPE;
    source: typeof WHATSAPP_ENTRY_SOURCE;
    waId: string;
    purpose: WhatsAppEntryPurpose;
    jti: string;
    iat?: number;
    exp?: number;
};

export type WhatsAppEntryStoredIntent = {
    userId: number;
    waId: string;
    purpose: WhatsAppEntryPurpose;
    createdAt: string;
};

export type WhatsAppEntryStatus = {
    purpose: WhatsAppEntryPurpose;
    whatsappPhone: string;
    identityType: 'technical_whatsapp' | 'linked_account';
    requiresAuthentication: boolean;
    canPasswordlessLogin: boolean;
    alreadyLoggedIn: boolean;
    accountConflict: boolean;
    expiresAt: string;
};

export function isWhatsAppEntryPurpose(value: unknown): value is WhatsAppEntryPurpose {
    return typeof value === 'string'
        && Object.values(WHATSAPP_ENTRY_PURPOSES).includes(value as WhatsAppEntryPurpose);
}

export function hasWhatsAppEntryTokenType(value: unknown): boolean {
    return !!value
        && typeof value === 'object'
        && (value as { type?: unknown }).type === WHATSAPP_ENTRY_TOKEN_TYPE;
}

export function isWhatsAppEntryJwtPayload(value: unknown): value is WhatsAppEntryJwtPayload {
    if (!value || typeof value !== 'object') return false;
    const payload = value as Partial<WhatsAppEntryJwtPayload>;
    return payload.type === WHATSAPP_ENTRY_TOKEN_TYPE
        && payload.source === WHATSAPP_ENTRY_SOURCE
        && Number.isInteger(payload.userId)
        && payload.userId > 0
        && typeof payload.waId === 'string'
        && payload.waId.length > 0
        && isWhatsAppEntryPurpose(payload.purpose)
        && typeof payload.jti === 'string'
        && payload.jti.length > 0;
}
